/**
 * Semantic colour tones, mapped to design tokens.
 *
 * "Enterprise" does not mean grey: colour should be *deliberate* —
 * carried by icon tiles, status chips, dots and the occasional value —
 * not by gradients, glows or filled pills. Every consumer (cards,
 * KPI tiles, badges) picks a tone here so the palette stays in
 * lock-step with `index.css` and dark mode.
 */
export type Tone =
  | "primary"
  | "accent"
  | "info"
  | "success"
  | "warning"
  | "destructive"
  | "neutral";

/** Tinted icon tile: soft fill, matching border, coloured glyph. */
export const TONE_TILE: Record<Tone, string> = {
  primary: "bg-primary/10 text-primary border-primary/20",
  accent: "bg-accent/10 text-accent border-accent/20",
  info: "bg-info/10 text-info border-info/20",
  success: "bg-success/10 text-success border-success/20",
  warning: "bg-warning/10 text-warning border-warning/20",
  destructive: "bg-destructive/10 text-destructive border-destructive/20",
  neutral: "bg-muted/60 text-foreground/70 border-border",
};

export const TONE_TEXT: Record<Tone, string> = {
  primary: "text-primary",
  accent: "text-accent",
  info: "text-info",
  success: "text-success",
  warning: "text-warning",
  destructive: "text-destructive",
  neutral: "text-muted-foreground",
};

export const TONE_DOT: Record<Tone, string> = {
  primary: "bg-primary",
  accent: "bg-accent",
  info: "bg-info",
  success: "bg-success",
  warning: "bg-warning",
  destructive: "bg-destructive",
  neutral: "bg-muted-foreground/50",
};

/** Map a legacy `text-success`-style class to a tone. */
export function toneFromTextClass(cls: string | undefined): Tone {
  const m = cls?.match(/text-(primary|accent|info|success|warning|destructive)/);
  return (m?.[1] as Tone) ?? "neutral";
}
